import { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  MapPin,
  Clock,
  Cpu,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Layers,
  Eye,
  Zap,
  ImageIcon,
  ArrowRight,
  ArrowLeft,
  Activity,
  Timer,
  Link2,
  Check,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import GoogleMap from "google-maps-react-markers";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { cn } from "@/ui/lib/utils";
import { activeLocale, formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { MAP_LIBRARIES } from "@/map/utils/mapConstants";
import {
  useGetEventDetail,
  useGetClassificationTaxonomy,
  useGetCycleDetail,
} from "../../api";
import { alertStatusStyle } from "@/alerts/utils/alert-status-style";
import { alertStatusLabel, alertTypeLabel } from "@/alerts/types/machine-alert";
import { eventStateLabel } from "../../utils/event-colors";
import { resolveLabelToFamily } from "../../utils/taxonomy";
import type { EventImage } from "../../types/event";
import type { ClassificationLabel } from "../../types/review";
import type { CycleEvent, RelatedCycleRef, CycleMetrics } from "../../types/cycle";

/* ──────────────────────────────────────────────────────────────────
 * EventPanel — unified cycle + event viewer.
 *
 * Layout concept: CYCLE is the primary entity, events are nested.
 *
 * Desktop (≥1024px):
 *   Header   — cycle identity + metric cards + time breakdown (full width)
 *   Body     — [event list (260px) | hero | event sidebar (300px)]
 *
 * Mobile (<1024px):
 *   Header   — compact cycle info + metrics
 *   Event nav — ◀ DUMP NO TRUCK 5/16 ▶
 *   Hero     — full width photo / map
 *   Filmstrip
 *   Sidebar sections — stacked vertically
 *
 * Keyboard:
 *   ← / →         prev / next frame (photo mode)
 *   ↑ / ↓         prev / next event in cycle (with photos)
 *   Shift+← / →   prev / next cycle
 *   Esc            close
 * ────────────────────────────────────────────────────────────────── */

interface EventPanelProps {
  eventUuid: string;
  activeImageId?: number | null;
  onClose: () => void;
  onOpenInDetail: (imageId: number) => void;
}

/* ─── Helpers ────────────────────────────────────────────────────── */

const fmtDur = (s: number, t: TFunction<"mlExports">) => {
  if (s >= 60)
    return t("event.duration.minutes", {
      minutes: formatNumber(Math.floor(s / 60)),
      seconds: formatNumber(Math.round(s % 60)),
    });
  return t("event.duration.seconds", {
    value: formatNumber(s, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  });
};

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString(activeLocale(), { hour: "2-digit", minute: "2-digit", second: "2-digit" });

const isRealEvent = (e: CycleEvent) => !e.state.includes("unknown");

const getTimeBreakdown = (metrics: CycleMetrics, t: TFunction<"mlExports">) => {
  const items = [
    { label: t("event.breakdown.dig"), time: metrics.dig_time, count: metrics.dig_cnt, color: "#9333EA" },
    { label: t("event.breakdown.dump"), time: metrics.dump_time, count: metrics.dump_cnt, color: "#DC2626" },
    { label: t("event.breakdown.swingLoaded"), time: metrics.swing_loaded_time, count: metrics.swing_loaded_cnt, color: "#16A34A" },
    { label: t("event.breakdown.swingEmpty"), time: metrics.swing_empty_time, count: metrics.swing_empty_cnt, color: "#2563EB" },
  ].filter((i) => i.time > 0);
  const total = items.reduce((s, i) => s + i.time, 0) || 1;
  return items.map((i) => ({ ...i, pct: i.time / total }));
};

/* ─── Map marker ─────────────────────────────────────────────────── */

const EventMapMarker = ({
  color,
  size = "sm",
}: {
  lat: number;
  lng: number;
  color: string;
  size?: "sm" | "lg";
}) => {
  const px = size === "lg" ? 6 : 4;
  return (
    <div className="relative -translate-x-1/2 -translate-y-1/2">
      <div
        className="rounded-full border-2 border-background"
        style={{
          width: `${px * 4}px`,
          height: `${px * 4}px`,
          backgroundColor: color,
          boxShadow: `0 0 ${px * 3}px ${color}80, 0 0 ${px * 6}px ${color}40`,
        }}
      />
      <div
        className="absolute inset-0 rounded-full animate-ping opacity-30"
        style={{ width: `${px * 4}px`, height: `${px * 4}px`, backgroundColor: color }}
      />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ═══════════════════════════════════════════════════════════════════ */

const EventPanel = ({
  eventUuid: initialEventUuid,
  activeImageId,
  onClose,
  onOpenInDetail,
}: EventPanelProps) => {
  const { t } = useTranslation("mlExports");
  const [currentEventUuid, setCurrentEventUuid] = useState(initialEventUuid);
  useEffect(() => { setCurrentEventUuid(initialEventUuid); }, [initialEventUuid]);

  const { data: detail, isLoading, isError } = useGetEventDetail(currentEventUuid);
  const { data: taxonomy } = useGetClassificationTaxonomy();

  const core = detail?.event ?? null;
  const label = core ? eventStateLabel(core.state, t) : null;
  const accent = core?.color ?? "#6B7280";
  const cycle = detail?.cycle ?? null;

  const { data: cycleDetail, isLoading: isCycleLoading } = useGetCycleDetail(cycle?.uuid);
  const cycleEvents = useMemo(
    () => cycleDetail?.events.filter(isRealEvent) ?? [],
    [cycleDetail],
  );
  const hasCycle = cycleEvents.length > 0;

  const currentEventIndex = useMemo(
    () => cycleEvents.findIndex((ev) => ev.uuid === currentEventUuid),
    [cycleEvents, currentEventUuid],
  );

  const [focusedImageId, setFocusedImageId] = useState<number | null>(
    activeImageId ?? null,
  );

  useEffect(() => {
    if (!detail) return;
    const stillThere = detail.images.some((img) => img.image_id === focusedImageId);
    if (!stillThere) setFocusedImageId(detail.images[0]?.image_id ?? null);
  }, [detail, focusedImageId]);

  const focusedIndex = useMemo(() => {
    if (!detail || focusedImageId == null) return -1;
    return detail.images.findIndex((img) => img.image_id === focusedImageId);
  }, [detail, focusedImageId]);

  const focusedImage: EventImage | null =
    focusedIndex >= 0 && detail ? detail.images[focusedIndex] : null;

  const hasImages = (detail?.images.length ?? 0) > 0;

  const filmstripRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!filmstripRef.current || focusedIndex < 0) return;
    const tile = filmstripRef.current.querySelector<HTMLElement>(
      `[data-frame-idx="${focusedIndex}"]`,
    );
    tile?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [focusedIndex]);

  const cycleListRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!cycleListRef.current) return;
    const el = cycleListRef.current.querySelector<HTMLElement>(`[data-event-active="true"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [currentEventUuid]);

  const goFrame = (delta: number) => {
    if (!detail || focusedIndex < 0) return;
    const next = focusedIndex + delta;
    if (next < 0 || next >= detail.images.length) return;
    setFocusedImageId(detail.images[next].image_id);
  };

  const goEvent = (uuid: string) => {
    setCurrentEventUuid(uuid);
    setFocusedImageId(null);
  };

  /** Navigate to adjacent event with photos */
  const goAdjacentEvent = (direction: -1 | 1) => {
    const idx = cycleEvents.findIndex((ev) => ev.uuid === currentEventUuid);
    if (direction === -1) {
      for (let i = idx - 1; i >= 0; i--) {
        if (cycleEvents[i].images.length > 0) { goEvent(cycleEvents[i].uuid); break; }
      }
    } else {
      for (let i = idx + 1; i < cycleEvents.length; i++) {
        if (cycleEvents[i].images.length > 0) { goEvent(cycleEvents[i].uuid); break; }
      }
    }
  };

  const goCycleNav = (ref: RelatedCycleRef | null) => {
    if (!ref) return;
    setNextCycleUuid(ref.uuid);
  };

  const [nextCycleUuid, setNextCycleUuid] = useState<string | null>(null);
  const { data: nextCycleData } = useGetCycleDetail(nextCycleUuid);
  useEffect(() => {
    if (!nextCycleData || !nextCycleUuid) return;
    const firstWithPhotos = nextCycleData.events.find((e) => isRealEvent(e) && e.images.length > 0);
    const target = firstWithPhotos ?? nextCycleData.events.find(isRealEvent);
    if (target) {
      setCurrentEventUuid(target.uuid);
      setFocusedImageId(null);
    }
    setNextCycleUuid(null);
  }, [nextCycleData, nextCycleUuid]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;

      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }

      if (e.shiftKey && e.key === "ArrowLeft") {
        e.preventDefault();
        goCycleNav(cycleDetail?.related_cycles.prev ?? null);
        return;
      }
      if (e.shiftKey && e.key === "ArrowRight") {
        e.preventDefault();
        goCycleNav(cycleDetail?.related_cycles.next ?? null);
        return;
      }

      if (e.key === "ArrowUp" && hasCycle) { e.preventDefault(); goAdjacentEvent(-1); return; }
      if (e.key === "ArrowDown" && hasCycle) { e.preventDefault(); goAdjacentEvent(1); return; }

      if (e.key === "ArrowLeft" && hasImages) { e.preventDefault(); goFrame(-1); return; }
      if (e.key === "ArrowRight" && hasImages) { e.preventDefault(); goFrame(1); return; }

      if (e.key === "e" || e.key === "E") {
        e.preventDefault();
        if (focusedImageId != null) onOpenInDetail(focusedImageId);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, focusedIndex, focusedImageId, hasImages, hasCycle, cycleEvents, currentEventUuid, cycleDetail]);

  const { familyLabels, otherLabels } = useMemo(() => {
    if (!focusedImage) return { familyLabels: [], otherLabels: [] };
    const family: Array<ClassificationLabel & { color: string; familyName: string; memberName: string }> = [];
    const other: ClassificationLabel[] = [];
    for (const l of focusedImage.labels) {
      const resolved = resolveLabelToFamily(l.final_label, taxonomy);
      if (resolved) {
        const member = resolved.family.members[resolved.memberIndex];
        family.push({ ...l, color: member.color, familyName: resolved.family.display_name, memberName: member.display_name });
      } else {
        other.push(l);
      }
    }
    return { familyLabels: family, otherLabels: other };
  }, [focusedImage, taxonomy]);

  const timeBreakdown = useMemo(
    () => cycleDetail ? getTimeBreakdown(cycleDetail.metrics, t) : [],
    [cycleDetail, t],
  );

  const [linkCopied, setLinkCopied] = useState(false);
  const copyLink = () => {
    const cycleUuid = cycle?.uuid ?? cycleDetail?.cycle.uuid;
    let url: string;
    if (cycleUuid) {
      url = `${window.location.origin}/machine/cycle/${cycleUuid}?event=${currentEventUuid}`;
    } else {
      // No cycle context — link straight to the event's own page.
      url = `${window.location.origin}/machine/event/${currentEventUuid}`;
    }
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  /* ─── Sidebar sections (reused in desktop right column & mobile stacked) ── */
  const sidebarContent = core && detail ? (
    <>
      {/* 1. Mini map */}
      {hasImages && core.geo.latitude != null && core.geo.longitude != null && (
        <div className="relative h-36 shrink-0 overflow-hidden">
          <GoogleMap
            apiKey="AIzaSyDOZxOGfdD3uTIvA1mF20K-x7CZoOuZsc0"
            libraries={MAP_LIBRARIES}
            defaultCenter={{ lat: core.geo.latitude, lng: core.geo.longitude }}
            defaultZoom={16}
            options={{ disableDefaultUI: true, zoomControl: false, mapTypeId: "satellite", gestureHandling: "none", draggable: false }}
          >
            <EventMapMarker lat={core.geo.latitude} lng={core.geo.longitude} color={accent} />
          </GoogleMap>
          <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1.5 px-2 py-1 rounded bg-background/60 backdrop-blur-sm">
            <MapPin className="h-2.5 w-2.5" style={{ color: accent }} />
            <span className="text-[9px] font-mono text-foreground">{core.geo.latitude.toFixed(5)}, {core.geo.longitude.toFixed(5)}</span>
          </div>
        </div>
      )}

      {/* 2. Metrics + Timing (combined) */}
      <div className="p-3.5 border-b border-border">
        <SectionHeader icon={<Activity className="h-3 w-3" />} label={t("event.section.event")} />
        <div className="grid grid-cols-2 gap-2 mt-2">
          <MiniGauge label={t("event.gauge.duration")} value={fmtDur(core.duration_seconds, t)} accent={accent} />
          <MiniGauge label={t("event.gauge.status")} value={core.productive ? t("event.gauge.productive") : t("event.gauge.unproductiveShort")} accent={accent} warn={!core.productive} />
        </div>
        {core.abnormal && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-500 mt-2">
            <Zap className="h-3 w-3 shrink-0" />
            <span className="font-medium">{t("event.abnormalDetected")}</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2.5 text-[9px] mt-3 pt-2.5 border-t border-border">
          <DataCell label={t("event.data.start")} value={fmtTime(core.start_time)} />
          <DataCell label={t("event.data.end")} value={fmtTime(core.end_time)} />
          <DataCell label={t("event.data.duration")} value={fmtDur(core.duration_seconds, t)} />
          <DataCell label={t("event.data.frames")} value={formatNumber(detail.total_images)} />
        </div>
      </div>

      {/* 3. Alerts */}
      {(detail.alerts?.length ?? 0) > 0 && (
        <div className="p-3.5 border-b border-border">
          <SectionHeader
            icon={<AlertTriangle className="h-3 w-3 text-amber-500" />}
            label={t("event.section.alerts")}
            labelColor="text-amber-500"
            badge={<span className="ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/20">{detail.alerts.length}</span>}
          />
          <div className="space-y-1.5 mt-2">
            {detail.alerts.map((alert) => (
              <div key={alert.id} className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[10px] font-semibold text-amber-500">{alertTypeLabel(alert.type)}</span>
                  <span className={cn("text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border", alertStatusStyle(alert.status))}>{alertStatusLabel(alert.status)}</span>
                </div>
                <div className="flex items-center gap-3 text-[9px] font-mono text-muted-foreground">
                  <span>{alert.origin}</span>
                  <span>{new Date(alert.timestamp).toLocaleTimeString(activeLocale())}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 4. Labels */}
      {focusedImage && (
        <div className="p-3.5 border-b border-border">
          <SectionHeader icon={<Eye className="h-3 w-3" />} label={t("event.section.labels")} />
          {familyLabels.length === 0 && otherLabels.length === 0 ? (
            <div className="flex items-center gap-2 py-2 mt-1.5 text-[10px] text-muted-foreground">
              <AlertTriangle className="h-3 w-3 text-amber-500/60" />
              <span>{t("event.noLabels")}</span>
            </div>
          ) : null}
          {familyLabels.length > 0 && (
            <div className="space-y-0.5 mt-2">
              {familyLabels.map((l) => (
                <div key={l.id} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent/50">
                  <div className="w-[3px] h-4 rounded-full shrink-0" style={{ backgroundColor: l.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] text-foreground leading-tight truncate">{l.memberName}</div>
                    <div className="text-[8px] text-muted-foreground leading-tight">{l.familyName}</div>
                  </div>
                  {l.review_state === "MODEL_CORRECTED" && (
                    <span className="text-[7px] font-bold uppercase tracking-wider text-amber-500 border border-amber-500/20 rounded px-1 py-px">{t("event.edited")}</span>
                  )}
                  {l.confidence != null && (
                    <span className="text-[10px] font-mono font-semibold tabular-nums shrink-0" style={{ color: l.color }}>{formatNumber(Math.round(l.confidence * 100))}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {otherLabels.length > 0 && (
            <div className={cn("flex flex-wrap gap-1", familyLabels.length > 0 && "mt-2 pt-2 border-t border-border")}>
              {otherLabels.map((l) => (
                <span key={l.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] bg-muted text-muted-foreground border border-border">
                  {l.final_label}
                  {l.confidence != null && <span className="font-mono">{t("event.percent", { value: formatNumber(Math.round(l.confidence * 100)) })}</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 6. Cycle — hide details when cycle is in header, show "not part of" when no cycle */}
      {!cycle && (
        <div className="p-3.5 border-b border-border">
          <SectionHeader icon={<Layers className="h-3 w-3" />} label={t("event.section.cycle")} />
          <div className="flex items-center gap-2 py-2 mt-1.5 text-[10px] text-muted-foreground">
            <AlertTriangle className="h-3 w-3" />
            <span>{t("event.notInCycle")}</span>
          </div>
        </div>
      )}

      {/* 7. IDs + Device */}
      <div className="p-3.5 border-b border-border">
        <SectionHeader icon={<Cpu className="h-3 w-3" />} label={t("event.section.device")} />
        <div className="mt-2 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-foreground font-medium">{core.machine_name}</span>
            <span className="text-[9px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">#{core.machine_id}</span>
          </div>
          <div className="text-[9px] font-mono text-muted-foreground break-all leading-relaxed">{core.device_mxid}</div>
          <div className="flex flex-col gap-1 pt-1 border-t border-border">
            <CopyableId label={t("event.idLabel.event")} uuid={currentEventUuid} />
            {cycle?.uuid && <CopyableId label={t("event.idLabel.cycle")} uuid={cycle.uuid} />}
          </div>
        </div>
      </div>

      {/* 8. Actions */}
      <div className="p-3.5 mt-auto space-y-1.5">
        <button
          className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border border-border bg-card hover:bg-accent transition-all group text-left"
          onClick={copyLink}
        >
          {linkCopied ? (
            <Check className="h-4 w-4 text-emerald-500 shrink-0" />
          ) : (
            <Link2 className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
          )}
          <div className="flex-1">
            <div className={cn("text-[11px] font-medium transition-colors", linkCopied ? "text-emerald-500" : "text-muted-foreground group-hover:text-foreground")}>
              {linkCopied ? t("event.linkCopied") : t("event.copyLink")}
            </div>
            <div className="text-[9px] font-mono text-muted-foreground/60 truncate">
              /machine/cycle/...?event=...
            </div>
          </div>
        </button>
        <button
          className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border border-border bg-card hover:bg-accent transition-all group text-left"
          onClick={() => {
            // Open the machine timeline at this frame's moment (falls back to
            // the event start) so it seeks + opens the event modal on arrival.
            const ts = new Date(
              focusedImage?.timestamp ?? core.start_time,
            ).getTime();
            window.open(`/machine/${core.machine_id}?ts=${ts}`, "_blank");
          }}
        >
          <Clock className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
          <div className="flex-1">
            <div className="text-[11px] font-medium text-muted-foreground group-hover:text-foreground transition-colors">{t("event.viewOnTimeline")}</div>
            <div className="text-[9px] font-mono text-muted-foreground/60">{core.machine_name} · {fmtTime(focusedImage?.timestamp ?? core.start_time)}</div>
          </div>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>
      </div>
    </>
  ) : null;

  /* ─── Hero area (reused in desktop center & mobile) ─────────── */
  const heroContent = core && detail ? (
    <div className="relative flex flex-col min-w-0 min-h-0 flex-1">
      <div className="flex-1 relative flex items-center justify-center bg-muted/30">
        {hasImages && focusedImage ? (
          <div className="relative flex items-center justify-center w-full h-full p-2 lg:p-4">
            <div className="absolute inset-24 rounded-3xl blur-3xl opacity-[0.06] pointer-events-none" style={{ backgroundColor: accent }} />
            <div className="relative max-h-full max-w-full rounded-xl overflow-hidden shadow-2xl">
              <img src={focusedImage.signed_url} alt={t("event.frameAlt", { frame: focusedImage.event_frame })} className="block max-h-[calc(100vh-280px)] lg:max-h-[calc(100vh-200px)] max-w-full object-contain" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold text-white/90" style={{ backgroundColor: `${accent}cc` }}>
                      {t("event.frameBadge", { frame: focusedImage.event_frame })}
                    </span>
                    <span className="text-[10px] font-mono text-white/60">
                      {new Date(focusedImage.timestamp).toISOString().slice(11, 23)}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-white/40">{t("event.framePosition", { index: focusedIndex + 1, total: detail.images.length })}</span>
                </div>
              </div>
            </div>
            <button onClick={() => goFrame(-1)} disabled={focusedIndex <= 0}
              className={cn("absolute left-2 lg:left-3 top-1/2 -translate-y-1/2 p-2 lg:p-2.5 rounded-full transition-all bg-background/60 backdrop-blur-sm border border-border text-muted-foreground hover:text-foreground hover:bg-background/80", focusedIndex <= 0 && "opacity-0 cursor-not-allowed")}>
              <ChevronLeft className="h-4 w-4 lg:h-5 lg:w-5" />
            </button>
            <button onClick={() => goFrame(1)} disabled={focusedIndex >= detail.images.length - 1}
              className={cn("absolute right-2 lg:right-3 top-1/2 -translate-y-1/2 p-2 lg:p-2.5 rounded-full transition-all bg-background/60 backdrop-blur-sm border border-border text-muted-foreground hover:text-foreground hover:bg-background/80", focusedIndex >= detail.images.length - 1 && "opacity-0 cursor-not-allowed")}>
              <ChevronRight className="h-4 w-4 lg:h-5 lg:w-5" />
            </button>
          </div>
        ) : (
          /* Non-photo event display */
          <div className="flex flex-col items-center justify-center gap-5 w-full max-w-lg mx-auto px-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl mb-3 border" style={{ backgroundColor: `${accent}12`, borderColor: `${accent}20` }}>
                <span className="h-8 w-8 rounded-full" style={{ backgroundColor: accent, boxShadow: `0 0 24px ${accent}40` }} />
              </div>
              <div className="text-lg font-bold uppercase tracking-wider text-foreground">{label}</div>
              <div className="text-xs font-mono text-muted-foreground mt-1">{t("event.hero.timeRange", { start: fmtTime(core.start_time), end: fmtTime(core.end_time), duration: fmtDur(core.duration_seconds, t) })}</div>
            </div>

            {core.geo.latitude != null && core.geo.longitude != null && (
              <div className="w-full rounded-xl overflow-hidden border border-border" style={{ height: "220px" }}>
                <GoogleMap
                  apiKey="AIzaSyDOZxOGfdD3uTIvA1mF20K-x7CZoOuZsc0"
                  libraries={MAP_LIBRARIES}
                  defaultCenter={{ lat: core.geo.latitude, lng: core.geo.longitude }}
                  defaultZoom={17}
                  options={{ disableDefaultUI: true, zoomControl: true, mapTypeId: "satellite", maxZoom: 22 }}
                >
                  <EventMapMarker lat={core.geo.latitude} lng={core.geo.longitude} color={accent} size="lg" />
                </GoogleMap>
              </div>
            )}

            <div className="flex flex-wrap gap-3 justify-center">
              <div className="px-4 py-2.5 rounded-lg border border-border bg-card text-center">
                <div className="text-[8px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{t("event.hero.status")}</div>
                <div className="text-xs font-mono text-foreground">{core.productive ? t("event.gauge.productive") : t("event.gauge.unproductive")}</div>
              </div>
              {core.geo.latitude != null && core.geo.longitude != null && (
                <div className="px-4 py-2.5 rounded-lg border border-border bg-card text-center">
                  <div className="text-[8px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{t("event.hero.position")}</div>
                  <div className="text-[10px] font-mono text-foreground">{core.geo.latitude.toFixed(5)}, {core.geo.longitude.toFixed(5)}</div>
                  {core.geo.altitude != null && (
                    <div className="text-[9px] font-mono text-muted-foreground mt-0.5">{t("event.hero.altitude", { value: formatNumber(core.geo.altitude, { maximumFractionDigits: 0 }) })}</div>
                  )}
                </div>
              )}
              {core.location_name && (
                <div className="px-4 py-2.5 rounded-lg border border-border bg-card text-center">
                  <div className="text-[8px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{t("event.hero.location")}</div>
                  <div className="text-xs font-mono text-foreground">{core.location_name}</div>
                </div>
              )}
            </div>

            {!core.geo.latitude && (
              <div className="text-[10px] text-muted-foreground">{t("event.hero.noData")}</div>
            )}
          </div>
        )}
      </div>

      {/* Filmstrip */}
      {hasImages && (
        <div ref={filmstripRef} className="flex gap-1.5 overflow-x-auto px-3 lg:px-4 py-2 lg:py-2.5 border-t border-border bg-muted/50 shrink-0 hide-scrollbar">
          {detail.images.map((img, idx) => {
            const isActive = img.image_id === focusedImageId;
            return (
              <button
                key={img.image_id}
                data-frame-idx={idx}
                onClick={() => setFocusedImageId(img.image_id)}
                className={cn(
                  "relative shrink-0 rounded-md overflow-hidden transition-all duration-200 h-14 w-14 lg:h-16 lg:w-16 hover:ring-1 hover:ring-ring/30",
                  isActive ? "ring-2 ring-offset-1 ring-offset-background scale-105" : "opacity-50 hover:opacity-80",
                )}
                style={isActive ? { outline: `2px solid ${accent}`, outlineOffset: "2px", boxShadow: `0 0 12px ${accent}30` } : undefined}
              >
                <img src={img.signed_url} alt={t("event.frameAlt", { frame: img.event_frame })} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                {isActive && <div className="absolute inset-x-0 bottom-0 h-0.5" style={{ backgroundColor: accent }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  ) : null;

  return (
    <AnimatePresence>
      <motion.div
        key="event-panel"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-50 flex flex-col bg-background"
      >
        {/* Accent line */}
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: hasCycle ? "#f59e0b" : accent }} />
        <div className="absolute top-0 left-0 right-0 h-24 pointer-events-none" style={{ background: `linear-gradient(to bottom, ${hasCycle ? "#f59e0b" : accent}12, transparent)` }} />

        {/* ─── Header ──────────────────────────────────────────── */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="relative shrink-0"
        >
          {hasCycle && cycleDetail ? (
            /* ── Cycle-primary header ─────────────────────── */
            <div className="px-3 lg:px-5 py-2 lg:py-2.5">
              {/* Row 1: back + cycle identity + kbd hints */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 lg:gap-3 min-w-0">
                  <button onClick={onClose} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors shrink-0" title={t("detail.header.close")}>
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="flex items-center gap-1.5">
                    <Layers className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-amber-500">{t("event.cycleBadge")}</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground truncate">
                    {cycleDetail.cycle.machine_name}
                    {cycleDetail.cycle.location_name && (
                      <span className="font-normal text-muted-foreground"> · {cycleDetail.cycle.location_name}</span>
                    )}
                  </span>
                  <span className="hidden sm:inline text-[10px] font-mono text-muted-foreground shrink-0">
                    {t("event.cycleTimeRange", {
                      start: fmtTime(cycleDetail.cycle.start_time),
                      end: fmtTime(cycleDetail.cycle.end_time),
                    })}
                  </span>
                </div>

                {/* Keyboard hints — desktop only */}
                <div className="hidden xl:flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground shrink-0">
                  {hasImages && (
                    <span className="inline-flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">←→</kbd>
                      <span>{t("event.keys.frame")}</span>
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 ml-2">
                    <kbd className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">↑↓</kbd>
                    <span>{t("event.keys.event")}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 ml-2">
                    <kbd className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">⇧←→</kbd>
                    <span>{t("event.keys.cycle")}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 ml-2">
                    <kbd className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">Esc</kbd>
                    <span>{t("event.keys.close")}</span>
                  </span>
                </div>
              </div>

              {/* Row 2: metric cards + time breakdown */}
              <div className="flex items-center gap-2 lg:gap-4 mt-2 overflow-x-auto hide-scrollbar">
                <div className="flex items-center gap-1.5 lg:gap-2 shrink-0">
                  <CycleDashCard label={t("event.dash.duration")} value={fmtDur(cycleDetail.cycle.duration_seconds, t)} />
                  <CycleDashCard
                    label={t("event.dash.productivity")}
                    value={t("event.percent", { value: formatNumber(Math.round(cycleDetail.cycle.productivity * 100)) })}
                    color={cycleDetail.cycle.productivity >= 0.7 ? "#22c55e" : cycleDetail.cycle.productivity >= 0.4 ? "#eab308" : "#ef4444"}
                  />
                  <CycleDashCard label={t("event.dash.events")} value={formatNumber(cycleEvents.length)} />
                  <CycleDashCard
                    label={t("event.dash.alerts")}
                    value={formatNumber(cycleDetail.alerts.length)}
                    color={cycleDetail.alerts.length > 0 ? "#f59e0b" : undefined}
                  />
                </div>

                {/* Time breakdown — hidden on small screens */}
                {timeBreakdown.length > 0 && (
                  <div className="hidden md:block flex-1 min-w-0 max-w-xs">
                    <div className="flex h-1.5 rounded-full overflow-hidden gap-px bg-muted">
                      {timeBreakdown.map((seg) => (
                        <div
                          key={seg.label}
                          className="h-full rounded-full"
                          style={{ width: `${Math.max(seg.pct * 100, 4)}%`, backgroundColor: seg.color }}
                          title={t("event.breakdown.title", {
                            label: seg.label,
                            time: fmtDur(seg.time, t),
                            percent: formatNumber(Math.round(seg.pct * 100)),
                          })}
                        />
                      ))}
                    </div>
                    <div className="flex gap-x-3 mt-1">
                      {timeBreakdown.map((seg) => (
                        <span key={seg.label} className="inline-flex items-center gap-1 text-[8px] font-mono text-muted-foreground whitespace-nowrap">
                          <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
                          {seg.label} {fmtDur(seg.time, t)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ── Event-only header (no cycle) ─────────────── */
            <div className="flex items-center justify-between px-3 lg:px-5 py-2.5">
              <div className="flex items-center gap-3 min-w-0">
                <button onClick={onClose} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" title={t("detail.header.close")}>
                  <X className="h-5 w-5" />
                </button>

                {core && label ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-bold tracking-widest uppercase text-white" style={{ backgroundColor: accent, boxShadow: `0 0 16px ${accent}40` }}>
                    <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
                    {label}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-bold tracking-widest uppercase text-muted-foreground bg-muted">{t("eventStates.fallback")}</span>
                )}

                {core && !core.productive && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-500 border border-amber-500/20 rounded px-2 py-0.5 bg-amber-500/5">{t("event.unprod")}</span>
                )}
                {core?.abnormal && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-500 border border-amber-500/20 rounded px-2 py-0.5 bg-amber-500/5 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {t("event.abnormal")}
                  </span>
                )}

                {core && (
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">
                      {core.machine_name}
                      {core.location_name && <span className="font-normal text-muted-foreground"> · {core.location_name}</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">
                      {t("event.hero.timeRange", {
                        start: fmtTime(core.start_time),
                        end: fmtTime(core.end_time),
                        duration: fmtDur(core.duration_seconds, t),
                      })}
                      {hasImages &&
                        t("event.headerFrames", {
                          count: detail?.total_images ?? 0,
                          value: formatNumber(detail?.total_images ?? 0),
                        })}
                      {focusedIndex >= 0 && detail && (
                        <span className="text-foreground">
                          {t("event.headerPosition", {
                            index: focusedIndex + 1,
                            total: detail.images.length,
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="hidden lg:flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground shrink-0">
                {hasImages && (
                  <span className="inline-flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">←→</kbd>
                    <span>{t("event.keys.frame")}</span>
                  </span>
                )}
                <span className="inline-flex items-center gap-1 ml-2">
                  <kbd className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">Esc</kbd>
                  <span>{t("event.keys.close")}</span>
                </span>
              </div>
            </div>
          )}
        </motion.div>

        {/* Divider */}
        <div className="border-b border-border shrink-0" />

        {/* ─── Mobile event navigator (< lg) ──────────────────── */}
        {hasCycle && core && label && (
          <div className="lg:hidden flex items-center gap-2 px-3 py-2 border-b border-border bg-card/50 shrink-0">
            <button
              onClick={() => goAdjacentEvent(-1)}
              disabled={!cycleEvents.slice(0, currentEventIndex).some((e) => e.images.length > 0)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-20"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex-1 flex items-center justify-center gap-2 min-w-0">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase text-white shrink-0" style={{ backgroundColor: accent }}>
                {label}
              </span>
              <span className="text-[11px] font-mono text-muted-foreground truncate">
                {t("event.mobileMeta", {
                  time: fmtTime(core.start_time),
                  duration: fmtDur(core.duration_seconds, t),
                })}
              </span>
              {currentEventIndex >= 0 && (
                <span className="text-[10px] font-mono text-muted-foreground/60 shrink-0">
                  {t("event.mobilePosition", {
                    index: currentEventIndex + 1,
                    total: cycleEvents.length,
                  })}
                </span>
              )}
            </div>
            <button
              onClick={() => goAdjacentEvent(1)}
              disabled={!cycleEvents.slice(currentEventIndex + 1).some((e) => e.images.length > 0)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-20"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* ─── Body ────────────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
                <Loader2 className="h-8 w-8 text-muted-foreground" />
              </motion.div>
            </div>
          ) : isError || !detail || !core ? (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <div className="p-4 rounded-full bg-muted">
                <AlertTriangle className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-sm text-muted-foreground">{t("event.notFound")}</div>
              <button onClick={onClose} className="text-xs text-primary hover:text-primary/80 transition-colors underline underline-offset-4">{t("event.goBack")}</button>
            </div>
          ) : (
            <>
              {/* ─── Desktop layout (≥ lg) ────────────────── */}
              <div className={cn(
                "h-full gap-0 overflow-hidden hidden lg:grid",
                hasCycle
                  ? "grid-cols-[260px_1fr_300px]"
                  : "grid-cols-[1fr_300px]",
              )}>
                {/* Left: Cycle event list */}
                {hasCycle && cycleDetail && (
                  <div className="border-r border-border flex flex-col overflow-hidden bg-card">
                    <div ref={cycleListRef} className="flex-1 overflow-y-auto">
                      {isCycleLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                        </div>
                      ) : (
                        <div className="relative py-1">
                          <div className="absolute left-[22px] top-3 bottom-3 w-px bg-border" />

                          {cycleEvents.map((ev) => {
                            const isActive = ev.uuid === currentEventUuid;
                            const evStateLabel = eventStateLabel(ev.state, t);
                            const hasPhotos = ev.images.length > 0;
                            const clickable = hasPhotos;

                            return (
                              <div
                                key={ev.uuid}
                                role={clickable ? "button" : undefined}
                                tabIndex={clickable ? 0 : undefined}
                                data-event-active={isActive ? "true" : undefined}
                                onClick={clickable ? () => goEvent(ev.uuid) : undefined}
                                className={cn(
                                  "w-full flex items-start gap-2.5 pl-3 pr-3 py-2 text-left transition-all relative",
                                  clickable && isActive && "bg-accent",
                                  clickable && !isActive && "hover:bg-accent/50 cursor-pointer",
                                  !clickable && "opacity-50",
                                )}
                              >
                                <div className="relative shrink-0 flex flex-col items-center" style={{ width: "20px" }}>
                                  <div
                                    className={cn(
                                      "rounded-full border-2 transition-all z-10 relative",
                                      isActive ? "h-3.5 w-3.5" : "h-2.5 w-2.5",
                                    )}
                                    style={{
                                      backgroundColor: isActive ? ev.color : "transparent",
                                      borderColor: ev.color,
                                      boxShadow: isActive ? `0 0 8px ${ev.color}60` : undefined,
                                      marginTop: isActive ? "2px" : "4px",
                                    }}
                                  />
                                </div>

                                <div className="flex-1 min-w-0 py-px">
                                  <div className="flex items-center gap-1.5">
                                    <span className={cn(
                                      "text-[10px] font-bold uppercase tracking-wider truncate transition-colors",
                                      isActive ? "text-foreground" : "text-muted-foreground",
                                    )}>
                                      {evStateLabel}
                                    </span>
                                    {hasPhotos && (
                                      <span className="inline-flex items-center gap-0.5 text-[9px] font-mono text-muted-foreground">
                                        <ImageIcon className="h-2.5 w-2.5" />{ev.images.length}
                                      </span>
                                    )}
                                    {ev.has_alert && <Zap className="h-2.5 w-2.5 text-amber-500 shrink-0" />}
                                  </div>
                                  <div className={cn(
                                    "text-[10px] font-mono mt-0.5",
                                    isActive ? "text-muted-foreground" : "text-muted-foreground/60",
                                  )}>
                                    {fmtTime(ev.start_time)}
                                    <span className="text-muted-foreground/40"> · </span>
                                    {fmtDur(ev.duration_seconds, t)}
                                  </div>
                                </div>

                                {isActive && clickable && (
                                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0 mt-1.5" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="px-3 py-2.5 border-t border-border shrink-0">
                      <div className="flex gap-1.5">
                        <CycleNavButton
                          direction="prev"
                          ref_data={cycleDetail.related_cycles.prev}
                          onClick={() => goCycleNav(cycleDetail.related_cycles.prev)}
                          isLoading={!!nextCycleUuid}
                        />
                        <CycleNavButton
                          direction="next"
                          ref_data={cycleDetail.related_cycles.next}
                          onClick={() => goCycleNav(cycleDetail.related_cycles.next)}
                          isLoading={!!nextCycleUuid}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Center: Hero */}
                {heroContent}

                {/* Right: Sidebar */}
                <div className="border-l border-border flex flex-col min-w-0 overflow-y-auto bg-card">
                  {sidebarContent}
                </div>
              </div>

              {/* ─── Mobile layout (< lg) ─────────────────── */}
              <div className="lg:hidden h-full overflow-y-auto flex flex-col">
                {/* Hero */}
                <div className="min-h-[40vh]">
                  {heroContent}
                </div>

                {/* Cycle nav — mobile */}
                {hasCycle && cycleDetail && (
                  <div className="flex gap-1.5 px-3 py-2 border-t border-border bg-card/50 shrink-0">
                    <CycleNavButton
                      direction="prev"
                      ref_data={cycleDetail.related_cycles.prev}
                      onClick={() => goCycleNav(cycleDetail.related_cycles.prev)}
                      isLoading={!!nextCycleUuid}
                    />
                    <CycleNavButton
                      direction="next"
                      ref_data={cycleDetail.related_cycles.next}
                      onClick={() => goCycleNav(cycleDetail.related_cycles.next)}
                      isLoading={!!nextCycleUuid}
                    />
                  </div>
                )}

                {/* Sidebar sections stacked */}
                <div className="bg-card border-t border-border">
                  {sidebarContent}
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ─── Section header ────────────────────────────────────────────── */
const SectionHeader = ({
  icon,
  label,
  labelColor,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  labelColor?: string;
  badge?: React.ReactNode;
}) => (
  <div className="flex items-center gap-2">
    <span className="text-muted-foreground">{icon}</span>
    <span className={cn("text-[9px] font-bold uppercase tracking-widest", labelColor ?? "text-muted-foreground")}>{label}</span>
    {badge}
  </div>
);

/* ─── Productivity badge ────────────────────────────────────────── */
const ProductivityBadge = ({ value }: { value: number }) => {
  const pct = Math.round(value * 100);
  const color = value >= 0.7 ? "#22c55e" : value >= 0.4 ? "#eab308" : "#ef4444";
  return (
    <span
      className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded"
      style={{ color, backgroundColor: `${color}12`, border: `1px solid ${color}20` }}
    >
      {pct}%
    </span>
  );
};

/* ─── Cycle nav button ──────────────────────────────────────────── */
const CycleNavButton = ({
  direction,
  ref_data,
  onClick,
  isLoading,
}: {
  direction: "prev" | "next";
  ref_data: RelatedCycleRef | null;
  onClick: () => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation("mlExports");
  const disabled = !ref_data || isLoading;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex-1 flex flex-col items-center py-2 rounded-md text-left transition-all border border-border",
        disabled
          ? "opacity-20 cursor-not-allowed"
          : "hover:bg-accent",
      )}
    >
      <div className="flex items-center gap-1 text-[9px] font-mono text-muted-foreground">
        {direction === "prev" && <ChevronLeft className="h-3 w-3" />}
        <span>{direction === "prev" ? t("event.nav.prev") : t("event.nav.next")}</span>
        {direction === "next" && <ChevronRight className="h-3 w-3" />}
      </div>
      {ref_data && (
        <div className="text-[8px] font-mono text-muted-foreground/60 mt-0.5">
          {t("event.nav.summary", {
            duration: fmtDur(ref_data.duration_seconds, t),
            value: formatNumber(ref_data.events_count),
          })}
        </div>
      )}
    </button>
  );
};

/* ─── Mini gauge ─────────────────────────────────────────────────── */
const MiniGauge = ({ label, value, accent, warn }: { label: string; value: string; accent: string; warn?: boolean }) => (
  <div className="rounded-lg border px-3 py-2 overflow-hidden" style={{ borderColor: warn ? "#f59e0b30" : `${accent}15`, backgroundColor: warn ? "#f59e0b04" : `${accent}04` }}>
    <div className="text-[8px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
    <div className="text-sm font-mono font-bold" style={{ color: warn ? "#f59e0b" : undefined }}>
      <span className={warn ? "" : "text-foreground"}>{value}</span>
    </div>
  </div>
);

/* ─── Cycle dashboard card ──────────────────────────────────────── */
const CycleDashCard = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <div
    className="rounded-lg border px-2.5 lg:px-3 py-1.5 lg:py-2 relative overflow-hidden"
    style={{
      borderColor: color ? `${color}25` : undefined,
      backgroundColor: color ? `${color}06` : undefined,
    }}
  >
    <div className="text-[7px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
    <div className="text-[13px] lg:text-[15px] font-mono font-bold mt-0.5 leading-tight" style={color ? { color } : undefined}>
      <span className={color ? "" : "text-foreground"}>{value}</span>
    </div>
  </div>
);

/* ─── Data cell ──────────────────────────────────────────────────── */
const DataCell = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="font-bold uppercase tracking-widest text-muted-foreground mb-0.5">{label}</div>
    <div className="font-mono text-foreground">{value}</div>
  </div>
);

/* ─── Copyable short ID ─────────────────────────────────────────── */
const CopyableId = ({ label, uuid }: { label: string; uuid: string }) => {
  const { t } = useTranslation("mlExports");
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(uuid);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      className="flex items-center gap-2 group text-left w-full rounded px-1 -mx-1 hover:bg-accent/50 transition-colors"
      title={t("event.copyId", { uuid })}
    >
      <span className="text-[8px] font-bold uppercase tracking-widest text-muted-foreground w-10 shrink-0">{label}</span>
      <span className="text-[9px] font-mono text-muted-foreground group-hover:text-foreground transition-colors truncate">
        {uuid.slice(0, 8)}
      </span>
      {copied ? (
        <Check className="h-2.5 w-2.5 text-emerald-500 shrink-0 ml-auto" />
      ) : (
        <Link2 className="h-2.5 w-2.5 text-muted-foreground/40 group-hover:text-muted-foreground shrink-0 ml-auto transition-colors" />
      )}
    </button>
  );
};

export default EventPanel;
